import classnames from "classnames";
import Link from "next/link";
import { useRouter } from "next/router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { GET_WATCH } from "@/apollo/queries/watch";
import { Button, FavoriteButton, ShareButton } from "@/components/Buttons";
import ChannelLinearEpisodesSlider from "@/components/ChannelLinearEpisodesSlider";
import ChatDisplay from "@/components/Chat/ChatDisplay";
import ExpandText from "@/components/ExpandText";
import Loading from "@/components/Loading";
import WatchMetaTags, { tags } from "@/components/MetaTags/WatchMetaTags";
import Palette from "@/components/Palette";
import StreaksPlayer from "@/components/Player/StreaksPlayer";
import WatchRecommend from "@/components/Rtoaster/WatchRecommend";
import TvShowEpisodesSlider from "@/components/TvShowEpisodesSlider";
import {
  useAuthUserQuery,
  useBreadcrumbs,
  useCopyrights,
  useDeviceInfoQuery,
  usePlayerEventHandler,
  usePrepareToPlay,
  usePrevious,
  useUnSubscribedStatusLazyQuery,
  useWatchQuery,
  useModal,
} from "@/hooks";
import config, { getStoreId } from "@/lib/config";
import {
  chatEnableErrorCodes,
  beforeDeliveryStartErrorCode,
} from "@/lib/constants";
import dayjs from "@/lib/day";
import { checkFullScreen } from "@/lib/playerUtils";
import routes from "@/lib/routes";
import withMiddlewares from "@/middlewares/withMiddlewares";
import styles from "@/styles/Watch.module.scss";

import ChatDisplayIcon from "../../svgs/chat_display.svg";

function WatchTags({ meta, isBeforeChatEnd, isChatAvailableMeta }) {
  const filterdTags = [
    meta.rating !== "G" ? meta.rating : null,
    meta?.genres ? meta.genres.map((g) => g.name).join("/") : null,
    meta.production_year ? `${meta.production_year}年` : null,
    meta.production_country ? meta.production_country.join(" ") : null,
    meta.duration ? `${Math.floor(meta.duration / 60)}分` : null,
    meta.delivery_end_at ? `${meta.delivery_end_at}まで配信` : null,
  ]
    .filter((v) => v)
    .join("・");
  return (
    <div className={styles.tags}>
      <span>{filterdTags}</span>
      {isBeforeChatEnd && isChatAvailableMeta && (
        <span className={styles.chatLabel}>チャットあり</span>
      )}
    </div>
  );
}

export default function Watch() {
  const playerRef = useRef();
  const { setBreadcrumbs } = useBreadcrumbs();
  const { setCopyrights } = useCopyrights();
  const router = useRouter();
  const watchId = useMemo(() => Number(router.query.id), [router?.query?.id]);
  const [, setPlayerReady] = useState(false);
  const prevWatchId = usePrevious(watchId);
  const [prevState, setPrevState] = useState();
  // チャットコンポーネントの有無
  const [chatEnable, setChatEnable] = useState(false);
  // チャットUI表示の有無
  const [chatDisplay, setChatDisplay] = useState(false);

  const { data, loading: userLoading } = useAuthUserQuery({
    fetchPolicy: "cache-and-network",
  });
  const user = data?.user;
  const { data: { deviceInfo } = {} } = useDeviceInfoQuery({
    fetchPolicy: "cache-first",
  });
  const { isAndroid, isMobile, isTablet } = deviceInfo ?? {};
  const { loading: watchLoading, data: watch } = useWatchQuery({
    variables: { id: watchId },
    fetchPolicy: "cache-and-network",
  });
  const { episode, nextEpisode, entitlements, howtoPlay } = watch || {};

  const loggedIn = !userLoading && data?.user;
  const isSVOD = !!howtoPlay.courses[0];
  const [unSubscribedStatus, { data: unSubscribedStatusData }] =
    useUnSubscribedStatusLazyQuery();

  const ignoreMetaId = parseInt(episode.id, 10);
  const [prepareToPlay, state] = usePrepareToPlay(episode, entitlements);
  const { hideModal } = useModal();

  const { loading, playbackAuth, mediaSource, rooms } = state;
  // チャット情報
  const isChatAvailableMeta = useMemo(
    () => rooms?.length > 0 && !isAndroid,
    [rooms, isAndroid]
  );
  const encodeStartDate = episode?.encode_start_date
    ? dayjs(episode?.encode_start_date)
    : null;
  const encodeEndDate = episode?.encode_end_date
    ? dayjs(episode?.encode_end_date)
    : null;
  const pageEntryTime = useMemo(() => dayjs(), []);
  const isLinearAvailable =
    !!episode?.source_epg_id &&
    encodeEndDate &&
    pageEntryTime.isBefore(encodeEndDate);
  const liveStartBefore =
    rooms?.length > 0 ? rooms[0]?.customData?.liveStartBefore || 0 : 0;
  const liveEndAfter =
    rooms?.length > 0 ? rooms[0]?.customData?.liveEndAfter || 0 : 0;
  const chatStartTime = encodeStartDate
    ? encodeStartDate.subtract(liveStartBefore, "minute")
    : null;
  const chatEndTime = encodeEndDate
    ? encodeEndDate.add(liveEndAfter, "minute")
    : null;
  // チャットの有効期間チェック
  const isValidChatPeriod = useMemo(() => {
    if (!chatStartTime || !chatEndTime) return false;
    const now = dayjs();
    return chatStartTime.isBefore(now) && chatEndTime.isAfter(now);
  }, [chatStartTime, chatEndTime]);
  // ラベル表示用、チャット終了前チェック
  const isBeforeChatEnd = useMemo(() => {
    if (!chatEndTime) return false;
    const now = dayjs();
    return chatEndTime.isAfter(now);
  }, [chatEndTime]);

  const handlePlayerReady = useCallback((player) => {
    playerRef.current = player;
    setPlayerReady(true);
  }, []);

  const handleEnded = useCallback(() => {
    if (nextEpisode) {
      state.displayOverlay({ reason: "next_episode" });
    }
  }, [nextEpisode, state]);

  const setChatDisplayState = useCallback((bool) => {
    setChatDisplay(bool);
    if (bool) {
      window.scrollTo({ top: 0 });
    }
  }, []);
  const turnOnChatDisplay = useCallback(() => {
    setChatDisplayState(true);
  }, [setChatDisplayState]);

  const playToken = useMemo(() => {
    if (mediaSource && mediaSource?.play_token) {
      return mediaSource.play_token;
    } else {
      return null;
    }
  }, [mediaSource]);

  useEffect(() => {
    if (loggedIn && isSVOD) {
      unSubscribedStatus({
        variables: { courseId: Number(howtoPlay.courses[0].id) },
      });
    }
  }, [loggedIn, isSVOD, data, howtoPlay, unSubscribedStatus]);

  useEffect(() => {
    setChatEnable(isChatAvailableMeta);
  }, [isChatAvailableMeta]);

  usePlayerEventHandler(
    user,
    userLoading,
    deviceInfo,
    playerRef.current,
    episode,
    entitlements,
    playbackAuth,
    playToken,
    state.displayOverlay,
    state.canplay,
    { ended: handleEnded }
  );

  useEffect(() => {
    const handler = (path) => {
      // 遷移先がwatchページの場合はリセット
      if (path.includes("/watch")) {
        setPrevState(state);
        state.reset();
      }
      hideModal();
    };
    router.events.on("routeChangeStart", handler);
    return () => {
      router.events.off("routeChangeStart", handler);
    };
  }, []);

  // パンクズ
  useEffect(() => {
    if (episode) {
      const { tvShow, channel } = episode;
      setCopyrights(episode.copyright);
      const storeId = getStoreId();
      const valuePlanId = config.valuePlanId.vod;
      const channelId = parseInt(channel?.id, 10);
      const breadcrumbs = [{ text: "ホーム", href: routes.home }];
      if (channel) {
        if (storeId === channelId) {
          breadcrumbs.push({ text: "単品商品", href: routes.store });
        } else if (valuePlanId === channelId) {
          breadcrumbs.push({
            text: config.valuePlan.text,
            href: routes.valuePlan,
          });
        } else {
          breadcrumbs.push({ text: "月額商品一覧", href: routes.channels });
          breadcrumbs.push({
            text: channel.name,
            href: routes.channelDetail(channel.id),
          });
        }
      }
      if (tvShow) {
        breadcrumbs.push({ text: tvShow.name, href: routes.title(tvShow.id) });
      }
      breadcrumbs.push({ text: episode.name });
      setBreadcrumbs(breadcrumbs);
    }
  }, [router, episode, setCopyrights, setBreadcrumbs]);

  // 配信開始になったらプレイヤー関連をリロードする
  const deliveryCheckIntervalRef = useRef(null);
  const [isDeliveryStartChecked, setIsDeliveryStartChecked] = useState(false);
  const checkDeliveryStart = useCallback(async () => {
    const deliveryStartAt = dayjs(episode.delivery_start_at);
    const now = dayjs();
    if (
      now.isAfter(deliveryStartAt) &&
      playbackAuth?.error?.code === beforeDeliveryStartErrorCode &&
      !isDeliveryStartChecked
    ) {
      await state.resetAndUpdate();
      let force = true;
      await prepareToPlay(deviceInfo, force);
      setIsDeliveryStartChecked(true);
    }
  }, [
    episode.delivery_start_at,
    playbackAuth?.error?.code,
    isDeliveryStartChecked,
    prepareToPlay,
    deviceInfo,
    state,
  ]);

  useEffect(() => {
    if (episode && playbackAuth?.error?.code === beforeDeliveryStartErrorCode) {
      clearInterval(deliveryCheckIntervalRef.current);
      deliveryCheckIntervalRef.current = setInterval(checkDeliveryStart, 15000);
    } else {
      clearInterval(deliveryCheckIntervalRef.current);
    }
    return () => {
      clearInterval(deliveryCheckIntervalRef.current);
    };
  }, [checkDeliveryStart, episode, playbackAuth]);

  // init
  useEffect(() => {
    const func = async () => {
      if (
        !userLoading &&
        !watchLoading &&
        !state.prepared &&
        !state.overlay &&
        episode
      ) {
        await prepareToPlay(deviceInfo);
      }
    };
    func();
    // prepareToPlayを依存関係に追加することをlintに要求されるが、追加すると無限ループするのでlint無視する
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    userLoading,
    watchLoading,
    state.prepared,
    state.overlay,
    episode,
    deviceInfo,
  ]);

  const entitleEndAt = useMemo(() => {
    if (isSVOD) {
      if (unSubscribedStatusData?.unSubscribedStatus?.withdraw_at) {
        const withdraw_at =
          unSubscribedStatusData?.unSubscribedStatus?.withdraw_at;
        const now = dayjs();
        if (now.isBefore(withdraw_at))
          return dayjs(withdraw_at).format("YYYY/M/D H:mm");
      }
    } else {
      if (entitlements) {
        const entitlement = entitlements.find(
          (entitlement) => entitlement.end_at
        );
        if (entitlement) {
          return dayjs(entitlement.end_at).format("YYYY/M/D H:mm");
        }
      }
    }
    return null;
  }, [entitlements, unSubscribedStatusData, isSVOD]);

  const isFullScreen = checkFullScreen();
  useEffect(() => {
    if (watchId && prevWatchId && watchId !== prevWatchId && !isFullScreen) {
      window.location.reload();
    }
  }, [watchId, prevWatchId, isFullScreen]);

  return (
    <>
      <WatchMetaTags episode={episode} />
      <div className={styles.container}>
        <section
          className={classnames(styles.playerContainer, {
            [styles.withChat]: chatDisplay,
          })}
        >
          <div
            className={classnames(styles.player, {
              [styles.withChat]: chatDisplay,
            })}
          >
            {(loading || userLoading) && <Loading fixed />}
            <StreaksPlayer
              onReady={handlePlayerReady}
              poster={episode?.thumbnailUrl}
              watch={watch}
              watchState={state}
              prevState={prevState}
              entitlements={entitlements}
              user={user}
              isChatAvailableMeta={isChatAvailableMeta}
              chatDisplay={chatDisplay}
              setChatDisplay={setChatDisplayState}
              isValidChatPeriod={isValidChatPeriod}
            />
          </div>
          {chatEnable && (
            <div className={styles.chat}>
              <ChatDisplay
                rooms={rooms}
                chatDisplay={chatDisplay}
                setChatDisplay={setChatDisplayState}
                setChatEnable={setChatEnable}
                metaId={episode.id}
                encodeEndDate={encodeEndDate}
                user={user}
              />
            </div>
          )}
        </section>
        {/* PCは表示、SPはチャット表示中に非表示 */}
        {(!chatDisplay || !(isMobile || isTablet)) && (
          <>
            <section className={styles.detail}>
              <div className={styles.info}>
                <div className={styles.header}>
                  <div className={styles.first}>
                    <>
                      <h1 className={styles.title}>{episode.name}</h1>
                      <WatchTags
                        meta={episode}
                        isBeforeChatEnd={isBeforeChatEnd}
                        isChatAvailableMeta={isChatAvailableMeta}
                      />
                    </>
                  </div>
                  <div className={styles.second}>
                    <div
                      className={classnames(styles.secondChild, styles.buttons)}
                    >
                      {entitleEndAt &&
                        (isSVOD ? (
                          <div className={styles.entitleend}>
                            <div>契約終了日時</div>
                            <div>{entitleEndAt}</div>
                          </div>
                        ) : (
                          <div className={styles.entitleend}>
                            <div>視聴可能期間</div>
                            <div>{entitleEndAt}まで</div>
                          </div>
                        ))}
                      <FavoriteButton metaId={episode.id} />
                      <ShareButton title={episode.name} />
                    </div>
                    <div className={styles.secondChild}>
                      {isChatAvailableMeta &&
                        isValidChatPeriod &&
                        (!playbackAuth?.error?.code ||
                          chatEnableErrorCodes.includes(
                            playbackAuth?.error?.code
                          )) &&
                        !chatDisplay && (
                          <Button
                            className={styles.chatDisplayButton}
                            onClick={turnOnChatDisplay}
                          >
                            <ChatDisplayIcon />
                          </Button>
                        )}
                    </div>
                  </div>
                </div>
                <ExpandText className={styles.synopsis}>
                  <p className={styles.description}>{episode.description}</p>
                  <div>{episode.cast}</div>
                  <div>{episode.staff}</div>
                  <div>{episode.copyright}</div>
                </ExpandText>
                {isChatAvailableMeta && isBeforeChatEnd && (
                  <div className={styles.chatNotice}>
                    チャット欄はLIVE中および前後30分のみご利用いただけます。コミュニティガイドラインをよく読んで、楽しくご利用ください。
                    <br />
                    <br />
                    <Link
                      href="/static/legal/chat_guideline/"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: "#F7F989" }}
                    >
                      {`${config.host.vod}/static/legal/chat_guideline/`}
                    </Link>
                    <br />
                    <br />
                    ※端末のスペック、メモリ容量によりチャットご利用中に動作が重くなる場合があります。
                    <br />
                    動作が重い場合はブラウザ以外のアプリを閉じる、ブラウザの他のタブを閉じる等ご対応いただいた上で画面リロードをお試しください。
                    <br />
                    上記で解消しない場合は画面をリロードいただいた後、チャット非表示で映像をお楽しみください。
                  </div>
                )}
              </div>
            </section>
            <section className={styles.episodes}>
              {isLinearAvailable && (
                <>
                  <ChannelLinearEpisodesSlider
                    header={{
                      text: "配信予定",
                      link: {
                        href: routes.channelDetail(
                          episode.channel.id,
                          "program"
                        ),
                      },
                    }}
                    id={parseInt(episode.channel.id, 10)}
                    ignoreMetaId={ignoreMetaId}
                  />
                </>
              )}

              {!isLinearAvailable && episode.tvShow && (
                <TvShowEpisodesSlider
                  header={{
                    text: "エピソード一覧",
                    link: { href: routes.title(episode.tvShow.id) },
                  }}
                  id={parseInt(episode.tvShow.id, 10)}
                  ignoreMetaId={ignoreMetaId}
                />
              )}
              {episode.recommend && <Palette id={episode.recommend} />}
            </section>

            <section>
              <WatchRecommend />
            </section>
          </>
        )}
      </div>
    </>
  );
}

export const getServerSideProps = withMiddlewares(
  async ({ query }, { serverProps, error, apolloClient }) => {
    try {
      const id = Number(query.id);
      if (isNaN(id)) {
        return { notFound: true };
      }
      const { data } = await apolloClient.query({
        query: GET_WATCH,
        variables: { id },
        fetchPolicy: "network-only",
      });

      if (data?.episode) {
        const metatags = tags(data.episode);
        return {
          props: {
            ...serverProps,
            error,
            metatags,
            initialApolloState: apolloClient.cache.extract(),
          },
        };
      } else {
        return { notFound: true };
      }
    } catch (e) {
      const errorProperties = Object.getOwnPropertyNames(e).reduce(
        (obj, key) => {
          obj[key] = e[key];
          return obj;
        },
        {}
      );
      console.error(JSON.stringify(errorProperties));
      throw e;
    }
  }
);
